import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ProgressBarModule } from 'primeng/progressbar';

import { PrintBolService } from '../../../../shared/bill-of-lading/print-bol.service';
import { DispatchEvent } from '../../store/dispatch.reducer';

@Component({
  selector: 'app-print-dispatch-bols-dialog',
  templateUrl: './print-dispatch-bols-dialog.component.html',
  styleUrls: ['./print-dispatch-bols-dialog.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, CheckboxModule, ButtonModule, ProgressBarModule]
})
export class PrintDispatchBolsDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  groupedEvents: { group: string, events: (DispatchEvent & { selected?: boolean })[] }[] = [];
  loading = false;
  progress = 0;
  status = '';
  private triggerElement: HTMLElement | null = null;

  constructor(
    public config: DynamicDialogConfig,
    public ref: DynamicDialogRef,
    private printBolService: PrintBolService,
    private el: ElementRef
  ) {}

  ngOnInit() {
    this.triggerElement = document.activeElement as HTMLElement;

    // Clone events to handle selection state without mutating store
    if (this.config.data?.events) {
        this.groupedEvents = JSON.parse(JSON.stringify(this.config.data.events));
        
        // Select all by default
        this.groupedEvents.forEach(group => {
            group.events.forEach(e => e.selected = true);
        });
    }
  }

  ngAfterViewInit() {
    // Move focus to the first interactive element (first p-checkbox in group.events)
    setTimeout(() => {
      const firstCheckbox = this.el.nativeElement.querySelector('p-checkbox input');
      if (firstCheckbox) {
        firstCheckbox.focus();
      }
    }, 100);
  }

  ngOnDestroy() {
    // Restore focus to the saved trigger
    if (this.triggerElement) {
      this.triggerElement.focus();
    }
  }

  @HostListener('keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.ref.close();
      event.preventDefault();
      return;
    }

    if (event.key === 'Tab') {
      const focusableElements = this.el.nativeElement.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      
      if (!focusableElements.length) {
        event.preventDefault();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement.focus();
          event.preventDefault();
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement.focus();
          event.preventDefault();
        }
      }
    }
  }

  areAllSelected(): boolean {
    return this.groupedEvents.every(group => 
      group.events.every((e: any) => e.selected)
    );
  }

  toggleAll(checked: boolean) {
    this.groupedEvents.forEach(group => {
      group.events.forEach((e: any) => e.selected = checked);
    });
  }

  isGroupSelected(group: any): boolean {
    return group.events.every((e: any) => e.selected);
  }

  toggleGroup(group: any, checked: boolean) {
      group.events.forEach(e => e.selected = checked);
  }

  async print() {
    const selectedEvents = this.groupedEvents
        .flatMap(g => g.events)
        .filter(e => e.selected)
        .map(e => {
            if (e?.event?.job?.id) {
                return { eventId: e.event.id, jobId: e.event.job.id };
            }
            return null;
        })
        .filter((e): e is { eventId: string; jobId: string } => e !== null);

    if (selectedEvents.length === 0) return;

    this.loading = true;
    this.progress = 0;
    this.status = 'Starting...';

    try {
        await this.printBolService.printEvents(selectedEvents, (status, value) => {
          this.status = status;
          this.progress = value;
        });
        this.ref.close();
    } catch (error: any) {
        this.status = `Error: ${error.message || 'Unknown error'}`;
        this.progress = 0;
    } finally {
        this.loading = false;
    }
  }
}
